import React from 'react';
import { useApiClient } from '~/api-client';
import { UnitQueueRowData } from '../../socket/stream-types';
import {
    NudgeObject,
    QueueViewsResource,
    UnitStateGroupEnum,
    QueueColumnFilter,
} from '@mark43/cad-api';
import { mapQueueClassesToAgencyTypeGlobalAttributes } from '../../queues/utils/map-queue-classes-to-agency-type-global-attributes';
import {
    CadNextSocketListener,
    createStreamListener,
    useSocketStatus,
    useStream,
} from '../../socket/use-stream';
import { toBooleanMap } from '../../common/helpers/utils';
import { UnitQueueStore } from '../store/unit-queue-store';
import { WorkspacePanel } from '../../panels/workspace-panel';
import { workspacePanelIsQueue } from '../../panels/workspace-queue-exists';
import { useQuery } from '@tanstack/react-query';
import { useDebounce } from '@cad-arc';
import { transaction } from 'mobx';
import { parseInt, uniq } from 'lodash';
import { PromiseBuffer } from '../../common/promises/promise-buffer';
import { useDispatchAreaStore } from '../../dispatch-areas/use-dispatch-areas';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';

/**
 * This should never be imported directly outside of unit-queue-state-provider
 */

export function useDeprecatedIntegratedUnitQueueStore({
    workspacePanel,
    dispatchAreaIds,
    isPinToPrimaryActive,
    store,
    unitTypeAttrIds,
    unitQueueFilters,
}: {
    workspacePanel: WorkspacePanel;
    dispatchAreaIds: number[];
    isPinToPrimaryActive: boolean;
    store: UnitQueueStore;
    unitTypeAttrIds: number[];
    unitQueueFilters: QueueColumnFilter[];
}): {
    store: UnitQueueStore;
} {
    workspacePanelIsQueue(workspacePanel);
    const dispatchAreaStore = useDispatchAreaStore();
    const enabledFeatures = useEnabledFeatures();
    const apiClient = useApiClient();
    const agencyTypeGlobalAttrIds = React.useMemo(() => {
        return mapQueueClassesToAgencyTypeGlobalAttributes(workspacePanel.queue.queueClasses);
    }, [workspacePanel.queue.queueClasses]);
    const [promiseBuffer] = React.useState(
        () =>
            new PromiseBuffer<number>({
                delay: 100,
                callback: (inputs) => {
                    return fetchUnitsUpdatesByIds({
                        unitIdsToUpdate: inputs,
                        dispatchAreaIds: dispatchAreaStore.getMonitoredAndOwnedDispatchAreaIds(),
                        isPinToPrimaryActive: !!store.workspacePanel.queue.pinBackupsToPrimaryUnits,
                        unitTypeAttrIds:
                            store.workspacePanel.queue.unitTypeFilters?.map(
                                (unitTypeFilter) => unitTypeFilter.attributeId
                            ) ?? [],
                        agencyTypeGlobalAttrIds,
                        unitQueueFilters,
                    });
                },
            })
    );

    const dispatchAreasMap: Record<number, boolean> = React.useMemo(() => {
        return toBooleanMap(dispatchAreaIds);
    }, [dispatchAreaIds]);

    const [debouncedUnitTypeAttrIds] = useDebounce(unitTypeAttrIds, 300);
    const [debouncedUnitQueueFilters] = useDebounce(unitQueueFilters, 300);

    const getInitialQueueStateQuery = useQuery({
        queryKey: [
            'get-initial-unit-queue-views',
            store.workspacePanel.queue.id,
            dispatchAreaIds,
            isPinToPrimaryActive,
            debouncedUnitTypeAttrIds,
            debouncedUnitQueueFilters,
        ],
        queryFn() {
            promiseBuffer.stop();
            promiseBuffer.reset();
            return apiClient.request<QueueViewsResource.GetActiveUnitQueueViews>({
                method: 'POST',
                url: 'queue_views/unit_queue_views/',
                data: {
                    isPinToPrimaryActive,
                    dispatchAreaIds,
                    agencyTypeGlobalAttrIds,
                    assignmentStatus: store.workspacePanel.queue.assignmentStatus,
                    unitTypeAttrIds,
                    queueColumnFilters: unitQueueFilters,
                    isCadUnitLevelDispositionsEnabled: false,
                },
                // TODO: Remove when query execution is fixed
                timeout: window.Cypress ? undefined : 60000,
            });
        },
        onSuccess(response) {
            if (response.data.data) {
                store.setRows(response.data.data);
            }
            promiseBuffer.start();
        },
        onError() {
            store.setIsError(true);
        },
        notifyOnChangeProps: [],
        enabled: false,
    });

    const refetch = getInitialQueueStateQuery.refetch;

    const socketStatusListener: CadNextSocketListener = React.useMemo(
        () => ({
            invokeOnInitialize: true,
            onSocketStatusChange: (socketStatusChangeEvent) => {
                if (socketStatusChangeEvent.status === 'CONNECTED') {
                    refetch();
                }
            },
        }),
        [refetch]
    );

    const { getSocketStatus } = useSocketStatus(socketStatusListener);

    React.useEffect(() => {
        if (getSocketStatus() === 'CONNECTED') {
            refetch();
        }
    }, [dispatchAreaIds, refetch, getSocketStatus]);

    const fetchUnitsUpdatesByIds = React.useCallback(
        ({
            unitIdsToUpdate,
            dispatchAreaIds,
            isPinToPrimaryActive,
            unitTypeAttrIds,
            unitQueueFilters,
            agencyTypeGlobalAttrIds,
        }: {
            unitIdsToUpdate: number[];
            dispatchAreaIds: number[];
            isPinToPrimaryActive: boolean;
            unitTypeAttrIds: number[];
            unitQueueFilters: QueueColumnFilter[];
            agencyTypeGlobalAttrIds: number[];
        }) => {
            return apiClient
                .request<QueueViewsResource.GetUnitQueueViewsByIds>({
                    method: 'POST',
                    url: 'queue_views/unit_queue_views/ids/',
                    data: {
                        isPinToPrimaryActive,
                        agencyTypeGlobalAttrIds,
                        assignmentStatus: store.workspacePanel.queue.assignmentStatus,
                        dispatchAreaIds,
                        unitIds: unitIdsToUpdate,
                        unitTypeAttrIds,
                        queueColumnFilters: unitQueueFilters,
                        isCadUnitLevelDispositionsEnabled: false,
                    },
                })
                .then((response) => {
                    const unitQueueViews = response.data.data;
                    if (unitQueueViews) {
                        transaction(() => {
                            const unitIdsInResponse = new Set(
                                unitQueueViews.map((unitQueueView) => unitQueueView.id)
                            );
                            const unitIdsWeAskedForThatDidntComeBackInResponse = unitIdsToUpdate.filter(
                                (unitId) => !unitIdsInResponse.has(unitId)
                            );
                            store.removeRows(unitIdsWeAskedForThatDidntComeBackInResponse);
                            store.upsertRows(unitQueueViews);
                        });
                    }
                });
        },
        [apiClient, store]
    );

    const getUnitIdsInStoreFromUnitNudges = React.useMemo(() => {
        return (unitNudges: NudgeObject[] = []): number[] => {
            return unitNudges
                .filter((unitNudge) => {
                    const passesDispatchAreaFilter =
                        (unitNudge.d && dispatchAreasMap[unitNudge.d]) ||
                        (unitNudge.ed && dispatchAreasMap[unitNudge.ed]) ||
                        (unitNudge.do && dispatchAreasMap[unitNudge.do]);
                    return (
                        !!unitNudge.id &&
                        (store.hasRowById(unitNudge.id) || passesDispatchAreaFilter)
                    );
                })
                .map((unitNudge) => {
                    return unitNudge.id;
                });
        };
    }, [store, dispatchAreasMap]);

    const getUnitIdsInStoreFromNonUnitNudges = React.useMemo(() => {
        return (nonUnitNudges: NudgeObject[] = []): number[] => {
            return nonUnitNudges
                .map((nonUnitNudge) => {
                    return nonUnitNudge.u;
                })
                .filter((unitId) => !!unitId && store.hasRowById(unitId)) as number[];
        };
    }, [store]);

    const getUnitIdsToUpdateFromUnitStatusNudges = React.useMemo(() => {
        return (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .filter((unitStatusNudge) => {
                    const isLoggedOn = unitStatusNudge.usg !== UnitStateGroupEnum.OFF_DUTY.value;
                    const passesAssignmentStatusFilter =
                        workspacePanel.queue.assignmentStatus === undefined ||
                        (workspacePanel.queue.assignmentStatus === true && unitStatusNudge.e) ||
                        (workspacePanel.queue.assignmentStatus === false && !unitStatusNudge.e);
                    const passesAgencyTypeFilter =
                        unitStatusNudge.g && agencyTypeGlobalAttrIds.includes(unitStatusNudge.g);
                    const passesDispatchAreaFilter =
                        (unitStatusNudge.d && dispatchAreasMap[unitStatusNudge.d]) ||
                        (unitStatusNudge.ed && dispatchAreasMap[unitStatusNudge.ed]);

                    return (
                        isLoggedOn &&
                        passesAssignmentStatusFilter &&
                        passesAgencyTypeFilter &&
                        passesDispatchAreaFilter
                    );
                })
                .map((unitStatusNudge) => unitStatusNudge.u) as number[];
        };
    }, [agencyTypeGlobalAttrIds, dispatchAreasMap, workspacePanel.queue.assignmentStatus]);

    const getUnitIdsToUpdateFromEventNudges = React.useCallback(
        (eventNudges: NudgeObject[] = []): number[] => {
            const getUnitIdsToUpdateFromTrackedEvents = (
                eventNudges: NudgeObject[] = []
            ): number[] => {
                return eventNudges
                    .filter((eventNudge) => {
                        return !!store.trackedEvents[eventNudge.id];
                    })
                    .flatMap((eventNudge) => {
                        return Object.keys(store.trackedEvents[eventNudge.id]);
                    })
                    .map((idString) => parseInt(idString));
            };

            // For units that are not in the queue but need to be updated based on an event's dispatch area ID
            const getUnitIdsToUpdateBasedOnEventDispatchArea = (
                eventNudges: NudgeObject[] = []
            ): number[] => {
                return eventNudges
                    .filter(
                        (eventNudge) =>
                            !!eventNudge.assignedUnitIds?.length &&
                            eventNudge.d !== undefined &&
                            dispatchAreasMap[eventNudge.d]
                    )
                    .flatMap((nudge) => nudge.assignedUnitIds ?? []);
            };

            return [
                ...new Set<number>([
                    ...getUnitIdsToUpdateFromTrackedEvents(eventNudges),
                    ...getUnitIdsToUpdateBasedOnEventDispatchArea(eventNudges),
                ]).values(),
            ];
        },
        [dispatchAreasMap, store.trackedEvents]
    );

    const getUnitIdsToUpdateFromEventRelatedNudges = React.useCallback(
        (eventRelatedNudges: NudgeObject[] = []): number[] => {
            return eventRelatedNudges
                .filter((eventRelatedNudge) => {
                    return eventRelatedNudge.e && !!store.trackedEvents[eventRelatedNudge.e];
                })
                .flatMap((eventRelatedNudge) => {
                    return Object.keys(store.trackedEvents[eventRelatedNudge.e as number]);
                })
                .map((idString) => parseInt(idString));
        },
        [store]
    );

    const getUnitIdsToEvictFromUnitStatusNudges = React.useCallback(
        (unitStatusNudges: NudgeObject[] = []): number[] => {
            return unitStatusNudges
                .filter((unitStatusNudge) => {
                    // nudge is for a unit that isn't in the store, no need to evict
                    if (unitStatusNudge.u && !store.hasRowById(unitStatusNudge.u)) {
                        return false;
                    }
                    // unit is logged off
                    if (unitStatusNudge.usg === UnitStateGroupEnum.OFF_DUTY.value) {
                        return true;
                    }
                    if (workspacePanel.queue.assignmentStatus === true && !unitStatusNudge.e) {
                        // unit is unassigned, but this queue is for assigned units
                        return true;
                    }
                    // unit is assigned, but this queue is for unassigned units
                    if (workspacePanel.queue.assignmentStatus === false && unitStatusNudge.e) {
                        return true;
                    }
                    // unit is not in d-area or assigned to event in d-area we're monitoring
                    if (
                        unitStatusNudge.d &&
                        !dispatchAreasMap[unitStatusNudge.d] &&
                        (!unitStatusNudge.ed || !dispatchAreasMap[unitStatusNudge.ed])
                    ) {
                        return true;
                    }
                })
                .map((unitStatusNudge) => unitStatusNudge.u) as number[];
        },
        [dispatchAreasMap, store, workspacePanel.queue.assignmentStatus]
    );

    const getOtherUnitIdsAssignedToSameEvents = React.useCallback(
        (unitIds: number[]) => {
            const units = uniq(unitIds)
                .map((unitId) => {
                    return store.listStore.getItemById(unitId);
                })
                .filter((item) => !!item) as UnitQueueRowData[];
            return uniq(
                units.reduce((acc, unit) => {
                    const eventId = unit.eventQueueView?.id;
                    let otherUnitIdsAssignedToSameEvent: number[] = [];
                    if (eventId) {
                        otherUnitIdsAssignedToSameEvent = Object.keys(
                            store.trackedEvents[eventId]
                        ).map((val) => parseInt(val));
                    }
                    return [...acc, ...otherUnitIdsAssignedToSameEvent];
                }, [] as number[])
            );
        },
        [store.listStore, store.trackedEvents]
    );

    const listener = React.useMemo(() => {
        if (enabledFeatures.updateUnitQueueStreamEnabled) {
            return createStreamListener({
                streamNames: ['updateUnitQueue'],
                onMessage: (message) => {
                    let unitIdsToUpdate = message.payload.updateUnitQueueDtos.map(
                        (dto) => dto.unitId
                    );
                    if (isPinToPrimaryActive) {
                        const otherUnitIdsAssignedToSameEvents = getOtherUnitIdsAssignedToSameEvents(
                            unitIdsToUpdate
                        );
                        unitIdsToUpdate = unitIdsToUpdate.concat(otherUnitIdsAssignedToSameEvents);
                    }
                    if (unitIdsToUpdate.length > 0) {
                        for (const unitIdToUpdate of unitIdsToUpdate) {
                            promiseBuffer.enqueue(unitIdToUpdate);
                        }
                    }
                },
            });
        }
        return createStreamListener({
            streamNames: ['nudge'],
            onMessage: (message) => {
                let unitIdsToUpdate = [
                    ...getUnitIdsToUpdateFromUnitStatusNudges(message.payload.map['USS']),
                    ...getUnitIdsToUpdateFromEventNudges(message.payload.map['E']),
                    ...getUnitIdsToUpdateFromEventRelatedNudges(message.payload.map['AERC']),
                    ...getUnitIdsInStoreFromUnitNudges(message.payload.map['U']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['UUL']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['ULI']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['UT']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['P']),
                    ...getUnitIdsInStoreFromNonUnitNudges(message.payload.map['USHIFT']),
                    ...getUnitIdsToEvictFromUnitStatusNudges(message.payload.map['USS']),
                ];
                if (isPinToPrimaryActive) {
                    const otherUnitIdsAssignedToSameEvents = getOtherUnitIdsAssignedToSameEvents(
                        unitIdsToUpdate
                    );
                    unitIdsToUpdate = unitIdsToUpdate.concat(otherUnitIdsAssignedToSameEvents);
                }
                if (unitIdsToUpdate.length > 0) {
                    for (const unitIdToUpdate of unitIdsToUpdate) {
                        promiseBuffer.enqueue(unitIdToUpdate);
                    }
                }
            },
        });
    }, [
        enabledFeatures.updateUnitQueueStreamEnabled,
        isPinToPrimaryActive,
        getOtherUnitIdsAssignedToSameEvents,
        promiseBuffer,
        getUnitIdsToUpdateFromUnitStatusNudges,
        getUnitIdsToUpdateFromEventNudges,
        getUnitIdsToUpdateFromEventRelatedNudges,
        getUnitIdsInStoreFromUnitNudges,
        getUnitIdsInStoreFromNonUnitNudges,
        getUnitIdsToEvictFromUnitStatusNudges,
    ]);

    // @ts-expect-error sockets-mark43 type issues dont work with dynamic streamNames in socketlistener
    useStream(listener);

    return {
        store,
    };
}
